import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import IndividualSection from "../components/services/IndividualSection";
import CoupleSection from "../components/services/CoupleSection";
import GroupInterventionsSection from "../components/services/GroupInterventionsSection";
import OrganizationsSection from "../components/services/OrganizationsSection";
import PublicSpeakingSection from "../components/services/PublicSpeakingSection";
import PartnersMarquee from "../components/PartnersMarquee";
import { serviceCategories } from "../data/servicesContent";
import { partnersByCategory } from "../data/partnersContent";

const DEFAULT_TAB = serviceCategories[0].id;

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const tabsSectionRef = useRef(null);

  // L'onglet actif est piloté par l'ancre de l'URL (/services#couple, ...),
  // ce qui permet au sous-menu "Services" de la navbar d'ouvrir directement
  // le bon onglet.
  const hash = location.hash.slice(1);
  const hashIsCategory = serviceCategories.some((c) => c.id === hash);
  const activeTab = hashIsCategory ? hash : DEFAULT_TAB;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // À chaque navigation : on descend jusqu'aux onglets si une catégorie est
  // demandée, sinon on remonte en haut. Un clic direct sur un onglet ne fait
  // pas défiler la page (state.fromTab). Défilement instantané à l'arrivée
  // sur la page, animé quand on change de catégorie depuis la page elle-même.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    // "instant" outrepasse le scroll-behavior: smooth global (App.css).
    const behavior = hasMountedRef.current ? "smooth" : "instant";
    hasMountedRef.current = true;
    if (location.state?.fromTab) return;
    if (hashIsCategory) {
      tabsSectionRef.current?.scrollIntoView({ behavior, block: "start" });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.key]);

  const handleTabChange = (value) => {
    navigate(`#${value}`, { replace: true, state: { fromTab: true } });
  };

  return (
    <div className="min-h-screen bg-[#F5F3EB]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[#F5F3EB] -z-10"></div>

        <div className="absolute top-20 right-10 w-72 h-72 bg-[#EAA93D]/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#21167D]/5 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto text-center">
          <div
            className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
          >
            <h1 className="font-display text-5xl md:text-6xl font-semibold text-[#21167D] mb-6">
              Mes Services
            </h1>
            <p className="font-accent italic text-xl text-[#4A4458] max-w-3xl mx-auto leading-relaxed">
              Un accompagnement sur mesure pour répondre à vos besoins
              spécifiques, seul·e, en couple, en groupe ou en organisation
            </p>
          </div>
        </div>
      </section>

      {/* Onglets de services */}
      <section ref={tabsSectionRef} className="py-12 px-4 bg-[#FDFBF5] scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="flex flex-wrap h-auto w-full justify-center gap-2 bg-transparent p-0 mb-12">
              {serviceCategories.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="px-4 py-2.5 rounded-full border border-[#DCB071]/40 bg-[#F5F3EB] text-[#4A4458] data-[state=active]:bg-[#21167D] data-[state=active]:text-[#F5F3EB] data-[state=active]:shadow-md text-sm md:text-base"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="individuel" className="mt-0 focus-visible:outline-none">
              <IndividualSection />
            </TabsContent>
            <TabsContent value="couple" className="mt-0 focus-visible:outline-none">
              <CoupleSection />
            </TabsContent>
            <TabsContent value="groupes" className="mt-0 focus-visible:outline-none">
              <GroupInterventionsSection />
            </TabsContent>
            <TabsContent value="organismes" className="mt-0 focus-visible:outline-none">
              <OrganizationsSection />
            </TabsContent>
            <TabsContent value="prises-de-parole" className="mt-0 focus-visible:outline-none">
              <PublicSpeakingSection />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#21167D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl font-semibold text-[#F5F3EB] mb-6">
            Prêt à franchir le pas ?
          </h2>
          <p className="font-accent italic text-xl text-[#F5F3EB]/85 mb-8 leading-relaxed">
            Réservez votre première consultation et commençons ensemble votre
            cheminement vers le mieux-être.
          </p>
          <Link to="/rendez-vous">
            <Button
              size="lg"
              className="bg-[#EAA93D] text-[#21167D] hover:bg-[#F5F3EB] hover:text-[#21167D] shadow-xl"
            >
              Prendre rendez-vous maintenant
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Partenaires de la catégorie affichée, juste avant le footer */}
      {partnersByCategory[activeTab] && (
        <PartnersMarquee
          key={activeTab}
          title={partnersByCategory[activeTab].title}
          partners={partnersByCategory[activeTab].partners}
        />
      )}
    </div>
  );
};

export default Services;
